import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { CustomTooltip } from "../../../../../components/common/tooltip/CusomTooltip";
import { setColor, setFont } from "../../../../../assets/styles/styles";
import Title from "../../../components/Title";
import Body from "../../../layout/Body";
import { CACHE_QUERY_OPTIONS } from "../../../../../constants";
import Typography from "../../../../../components/common/text/Typography";
import { default as Button } from "../../../../../components/common/button/Button";
import { ReactComponent as ChevronIcon } from "../../../../../assets/images/icons/Chevron.svg";
import Modal from "../../../components/Modal";
import { ReactComponent as EditIcon } from "../../../../../assets/images/icons/editIcon.svg";
import { getArcadeGames, updateArcadeGames } from "../api/roomController";
import { ReactComponent as Checkmark } from "../../../../../assets/images/icons/Checkmark.svg";
import { ReactComponent as Cross } from "../../../../../assets/images/icons/Cross.svg";
import AddUpdateArcadeGames from "./AddUpdateArcadeGames";

const RoomArcadeGames = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [source, setSource] = useState("");
    const [gameId, setGameId] = useState("");
    const [gameTitle, setGameTitle] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [showBtnChmk, setShowBtnChmk] = useState(false);
    const [showBtnChmkSub, setShowBtnChmkSub] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedGame, setSelectedGame] = useState(null);
    const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
    const [jsonEditorValue, setJsonEditorValue] = useState("");

    const handleBack = () => {
        navigate(`/console/rooms/${id}`);
    };

    const {
        data: arcadeData,
        error: gameErr,
        isLoading: gameLdng,
    } = useQuery(["games", id], () => getArcadeGames(id), {
        enabled: !!id,
        ...CACHE_QUERY_OPTIONS(),
    });

    useEffect(() => {
        if (arcadeData) {
            setSource(arcadeData.source);
            setGameTitle(arcadeData.title);
            setSubtitle(arcadeData.subtitle);
        }
    }, [arcadeData]);
    const handleCloseClick = (field, value) => {
        if (field === "title") {
            setGameTitle(arcadeData.title);
            setShowBtnChmk(false);
        }
        if (field === "subtitle") {
            setSubtitle(arcadeData.subtitle);
            setShowBtnChmkSub(false);
        }
    };

    const handleContentClick = (e, field) => {
        const { value } = e.target;
        if (field === "gameTitle") {
            setGameTitle(value);
            setShowBtnChmk(true);
        }
        if (field === "subtitle") {
            setSubtitle(value);
            setShowBtnChmkSub(true);
        }
    };

    const handleCheckmarkClick = async (field, value) => {
        const data = {
            [field]: value,
        };
        if (field === "gameTitle") {
            setShowBtnChmk(false);
            setIsLoading(true);
            await updateArcadeGames(id, data);
            setIsLoading(false);
        }
        if (field === "subtitle") {
            setShowBtnChmkSub(false);
            setIsLoading(true);
            await updateArcadeGames(id, data);
            setIsLoading(false);
        }
        if (gameErr) {
            console.error("Error updating game:", gameErr);
            setSource(arcadeData.source);
        }
    };

    const images = require.context("../../../../../features/arcadeGames/arcade-icons", false, /\.png$/);
    const getImagePath = (imageName) => {
        try {
            return images(`./${imageName}.png`);
        } catch (error) {
            console.error(`Image ${imageName} not found, using default.`);
            return images("./game-default.png");
        }
    };
    const handleEditGame = () => {
        setSelectedGame(arcadeData);
        setShowModal(true);
    };

    return (
        <>
            <Title
                title={"ARCADE GAMES"}
                description={
                    "Manage the arcade games in this room. Add, edit, or remove games. Room has default games, which will update all default games in other rooms too."
                }
            />

            <UserConsoleText style={{ marginBottom: "1rem" }} onClick={handleBack}>
                <StyledIconPrev /> BACK TO TABLE
            </UserConsoleText>

            <Body isLoading={isLoading}>
                <>
                    <CardTable>
                        <TableRow>
                            <TableColumn>
                                <Typography variant="PinkText">
                                    <strong>Source: {source}</strong>
                                </Typography>
                                <Typography variant="ConsoleDescription">
                                    <strong>ID: {id} </strong>
                                </Typography>
                            </TableColumn>

                            <TableColumnButton>
                                <Button
                                    variant={"SmallButton"}
                                    label="UPDATE GAMES"
                                    buttonWidth="auto"
                                    margin="0rem 1rem 0rem 0rem"
                                    onClick={handleEditGame}
                                    border={`${setColor.accent4}`}
                                />
                            </TableColumnButton>
                        </TableRow>
                    </CardTable>

                    <CardTable>
                        <TableRow>
                            <TableColumn>
                                <Label>Game Title</Label>
                                <InputBox
                                    type="text"
                                    value={gameTitle}
                                    onChange={(e) => handleContentClick(e, "gameTitle")}
                                />
                                {showBtnChmk && (
                                    <>
                                        <StyledCheckmark onClick={() => handleCheckmarkClick("gameTitle", gameTitle)} />
                                        <StyledCloseIcon
                                            onClick={() => gameTitle && handleCloseClick("gameTitle", gameTitle)}
                                        />
                                    </>
                                )}
                            </TableColumn>
                            <TableColumn>
                                <Label>Subtitle</Label>
                                <InputBox
                                    type="text"
                                    value={subtitle}
                                    onChange={(e) => handleContentClick(e, "subtitle")}
                                />
                                {showBtnChmkSub && (
                                    <>
                                        <StyledCheckmark onClick={() => handleCheckmarkClick("subtitle", subtitle)} />
                                        <StyledCloseIcon
                                            onClick={() => subtitle && handleCloseClick("subtitle", subtitle)}
                                        />
                                    </>
                                )}
                            </TableColumn>
                        </TableRow>
                    </CardTable>

                    <CardTable>
                        <Grid itemCount={arcadeData?.gamesList?.length}>
                            {arcadeData?.gamesList?.map((game, index) => (
                                <CustomTooltip key={index} placement="bottom" title={game.tooltipText}>
                                    <GameWrapper>
                                        {/* <StyledEditIcon onClick={() => handleEditGame(game)} /> */}
                                        <GameIcon>
                                            <img
                                                height={50}
                                                width={50}
                                                style={{ justifyContent: "center" }}
                                                src={getImagePath(game.icon)}
                                                alt={game.name || "Game Icon"}
                                            />
                                            <p>{game.name || "Free Game"}</p>
                                        </GameIcon>
                                    </GameWrapper>
                                </CustomTooltip>
                            ))}
                        </Grid>
                    </CardTable>
                </>
            </Body>

            {showModal && (
                <Modal
                    isOpen={showModal}
                    setIsOpen={setShowModal}
                    children={
                        <AddUpdateArcadeGames
                            roomId={id}
                            action="Update"
                            source={source}
                            closeModal={() => setShowModal(false)}
                            data={selectedGame}
                        />
                    }
                />
            )}
        </>
    );
};

export default RoomArcadeGames;

const UserConsoleText = styled.h1`
    font-family: "Barlow Condensed", sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    width: fit-content;
    color: ${setColor.accent4};
    position: relative;
    cursor: pointer;
`;

const StyledIconPrev = styled(ChevronIcon)`
    transition: transform 0.3s;
    transform: rotate(180deg);
    border-radius: 5px !important;
    margin-top: 12px !important;
    transform: rotate(90deg) !important;
`;

const CardTable = styled.div`
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: column;
    margin: 0rem 1rem 1rem 1rem;
    padding: 20px 20px 20px 20px;
    color: ${setColor.inputText};
    background: ${setColor.backgroundGrey};
    border-radius: 5px;
`;

const TableRow = styled.div`
    display: flex;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TableColumn = styled.div`
    flex: 1;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const TableColumnButton = styled.div`
    justify-content: flex-end;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;
const InputBox = styled.input`
    padding: 10px;
    flex: 1;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    color: ${setColor.inputText};
    width: 80%;
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "1rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const StyledCheckmark = styled(Checkmark)`
    right: 15px;
    height: 20px;
    cursor: pointer;
    margin-top: 10px;
    &:hover {
        #Union {
            fill: ${setColor.greenLight};
        }
    }
`;

const StyledCloseIcon = styled(Cross)`
    border-radius: 5px !important;
    width: 20px;
    height: 20px;
    cursor: pointer;
    margin-top: 10px;
    right: 10px;
    &:hover {
        #Union {
            fill: ${setColor.lightPink};
        }
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 20px;
    flex-wrap: wrap;
    box-sizing: border-box;
    justify-items: center;

    @media (max-width: 920px) {
        grid-template-rows: repeat(auto-fit, minmax(80px, 1fr));
        grid-auto-columns: 1fr;
        grid-template-columns: repeat(2, auto);
        gap: 10px;
        margin-top: 0.5rem;
    }

    ${({ itemCount }) =>
        itemCount < 4 &&
        `
        grid-template-columns: repeat(${itemCount}, 1fr);
    `}
`;

const GameIcon = styled.div`
    width: 200px;
    height: 170px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    border-radius: 7.667px;
    border: 2px solid #3d364f;
    background: #23173e;

    & p {
        color: #fbfcff;
        font-family: ${setFont.barlow};
        font-weight: 500;
        font-size: 18px;
        margin-top: 0.7rem;
        letter-spacing: normal;
    }

    &:hover {
        transform: scale(1.05);
    }

    @media (max-width: 920px) {
        width: 150px;
        height: 150px;
    }
`;

const StyledEditIcon = styled(EditIcon)`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 15px;
    height: 15px;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
`;

const GameWrapper = styled.div`
    position: relative;
    &:hover ${StyledEditIcon} {
        opacity: 1;
    }
`;
